import React from 'react';
import {
	Header as HeaderDI,
	Section as SectionDI,
	SkeletonItem as SkeletonItemDI,
} from '@atlaskit/side-navigation';

import { JSErrorBoundary as JsErrorBoundaryDI } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { useIntlV2 as useIntlDI } from '@atlassian/jira-intl/src/v2/use-intl.tsx';
import { MenuItem as MenuItemDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/menu-item/main.tsx';
import { useBoard as useBoardDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/board/index.tsx';
import { useProject as useProjectDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/project/index.tsx';
import { withThrowOnError } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/throw-when-no-data-and-error/index.tsx';
import { useReports as useReportsDI } from '../../../controllers/reports/index.tsx';
import { DevOpsReports as DevOpsReportsDI } from './devops-reports/index.tsx';
import { messages } from './messages.tsx';
import type { ReportsProps } from './types.tsx';

const useThrowBoardDI = withThrowOnError(useBoardDI);
const useThrowProjectDI = withThrowOnError(useProjectDI);
const useThrowReportsDI = withThrowOnError(useReportsDI);

export const Reports = ({
	Header = HeaderDI,
	MenuItem = MenuItemDI,
	Section = SectionDI,
	SkeletonItem = SkeletonItemDI,
	useBoard = useThrowBoardDI,
	useIntl = useIntlDI,
	useProject = useThrowProjectDI,
	useReports = useThrowReportsDI,
	JsErrorBoundary = JsErrorBoundaryDI,
	DevOpsReports = DevOpsReportsDI,
}: ReportsProps) => {
	return (
		<ReportsSidebar
			Header={Header}
			MenuItem={MenuItem}
			Section={Section}
			SkeletonItem={SkeletonItem}
			useBoard={useBoard}
			useIntl={useIntl}
			useProject={useProject}
			useReports={useReports}
			JsErrorBoundary={JsErrorBoundary}
			DevOpsReports={DevOpsReports}
		/>
	);
};

const ReportsSidebar = ({
	Header = HeaderDI,
	MenuItem = MenuItemDI,
	Section = SectionDI,
	SkeletonItem = SkeletonItemDI,
	useBoard = useThrowBoardDI,
	useIntl = useIntlDI,
	useProject = useThrowProjectDI,
	useReports = useThrowReportsDI,
	JsErrorBoundary = JsErrorBoundaryDI,
	DevOpsReports = DevOpsReportsDI,
}: ReportsProps) => {
	const { formatMessage } = useIntl();
	const { data: reports } = useReports();
	const { data: board } = useBoard();
	const { data: project } = useProject();

	// Team solaris should be monitoring this, not navigation team.
	const devOpsErrorBoundaryProps = {
		teamName: 'fusion-solaris',
		// We don't want to blow-up anything, but just not to render devOps nav group only.
		fallback: 'unmount',
		packageName: 'navigationAppsSidebarSoftwareNextGen',
		id: 'nextGenDevOpsMenuGroup',
	};

	if (reports == null || board == null || project == null) return <SkeletonItem />;

	return (
		<Section>
			<Header>{formatMessage(messages.title)}</Header>
			{reports.hasOverview && (
				<MenuItem
					analytics={{ itemId: 'overview' }}
					selectedOn={['software-reports']}
					href={`/jira/software/projects/${project.key}/boards/${board.id}/reports`}
				>
					{formatMessage(messages.itemOverview)}
				</MenuItem>
			)}
			{reports.hasBurnup && (
				<MenuItem
					analytics={{ itemId: 'burnup' }}
					selectedOn={['software-reports-burnup']}
					href={`/jira/software/projects/${project.key}/boards/${board.id}/reports/burnup`}
				>
					{formatMessage(messages.itemBurnup)}
				</MenuItem>
			)}
			{reports.hasBurndown && (
				<MenuItem
					analytics={{ itemId: 'burndown' }}
					selectedOn={['software-reports-burndown']}
					href={`/jira/software/projects/${project.key}/boards/${board.id}/reports/burndown?source=sidebar`}
				>
					{formatMessage(messages.itemBurndown)}
				</MenuItem>
			)}
			{reports.hasVelocity && (
				<MenuItem
					analytics={{ itemId: 'velocity' }}
					selectedOn={['software-reports-velocity']}
					href={`/jira/software/projects/${project.key}/boards/${board.id}/reports/velocity`}
				>
					{formatMessage(messages.itemVelocity)}
				</MenuItem>
			)}
			{reports.hasCumulative && (
				<MenuItem
					analytics={{ itemId: 'cumulative' }}
					selectedOn={['software-reports-cumulative']}
					href={`/jira/software/projects/${project.key}/boards/${board.id}/reports/cumulative`}
				>
					{formatMessage(messages.itemCumulative)}
				</MenuItem>
			)}
			{/* @ts-expect-error - TS2769 - No overload matches this call. */}
			<JsErrorBoundary {...devOpsErrorBoundaryProps}>
				<DevOpsReports />
			</JsErrorBoundary>
			{reports.hasControl && (
				<MenuItem
					analytics={{ itemId: 'control' }}
					selectedOn={['software-reports-control']}
					href={`/jira/software/projects/${project.key}/boards/${board.id}/reports/control`}
				>
					{formatMessage(messages.itemControl)}
				</MenuItem>
			)}
		</Section>
	);
};
